import { useRef, useState, useEffect } from 'react';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import type { InputField } from '@/hooks/useRealtimeWorkflows';
import { Dices, ImagePlus, FileVideo, FileAudio, X, Loader2, ChevronDown } from 'lucide-react';
import { useFileUpload } from '@/hooks/useFileUpload';
import { acceptFor, matchesKind, type UploadKind } from '@/lib/uploadPolicy';

interface UploadedFileData {
  storagePath: string;
  fileName: string;
  file?: File; // For preview purposes
}

interface FieldRendererProps {
  field: InputField;
  value?: unknown;
  onChange?: (value: unknown) => void;
  readOnly?: boolean;
  previousFileName?: string;
}

export default function FieldRenderer({ field, value, onChange, readOnly = true, previousFileName }: FieldRendererProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const { uploadFile, uploadProgress } = useFileUpload();

  useEffect(() => {
    const uploadedData = value as UploadedFileData | undefined;
    if (uploadedData?.file && field.widget === 'file-image') {
      const url = URL.createObjectURL(uploadedData.file);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setPreviewUrl(null);
  }, [value, field.widget]);

  const fieldLabel = field.label || field.inputName;
  const displayValue = value ?? field.defaultValue;

  const handleChange = (v: unknown) => {
    if (onChange && !readOnly) onChange(v);
  };

  // Shared upload path for both click-to-browse and drag-and-drop.
  const handleFile = async (file: File | undefined) => {
    if (!file || readOnly) return;
    try {
      const uploaded = await uploadFile(file);
      handleChange({
        storagePath: uploaded.storagePath,
        fileName: uploaded.fileName,
        file, // Keep for preview
      } as UploadedFileData);
    } catch (err) {
      console.error('Upload failed:', err);
    }
  };

  const uploadKind: UploadKind = field.widget === 'file-image' ? 'image' : field.widget === 'file-video' ? 'video' : 'audio';

  const hasMultipleOptions = field.widget === 'select' && (field.options?.length ?? 0) > 1;

  return (
    <div className="space-y-2.5">
      {/* Label */}
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-foreground flex items-center gap-1.5">
          {fieldLabel}
          {field.required && <span className="text-violet-500 ml-0.5">*</span>}
          {hasMultipleOptions && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 font-normal">
              {field.options!.length} options
            </Badge>
          )}
        </label>
        {field.tooltip && (
          <span className="text-[10px] text-muted-foreground max-w-[200px] truncate italic">{field.tooltip}</span>
        )}
      </div>
      {/* Description */}
      {field.description && <p className="text-xs text-muted-foreground leading-relaxed -mt-1">{field.description}</p>}

      {/* Text input */}
      {field.widget === 'text' && (
        <input
          readOnly={readOnly}
          value={String(displayValue)}
          onChange={(e) => handleChange(e.target.value)}
          className={
            'w-full h-11 rounded-xl border border-border/80 ' +
            'bg-background px-4 text-sm text-foreground ' +
            'focus:outline-none focus:ring-2 focus:ring-violet-500/30 ' +
            'focus:border-violet-400 transition-all'
          }
        />
      )}

      {/* Textarea — larger, more inviting */}
      {field.widget === 'textarea' && (
        <Textarea
          readOnly={readOnly}
          value={String(displayValue)}
          onChange={(e) => handleChange(e.target.value)}
          placeholder="Enter your creative prompt..."
          className={
            'bg-background min-h-[120px] rounded-xl border-border/80 ' +
            'text-sm leading-relaxed resize-none ' +
            'focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400'
          }
        />
      )}

      {/* Number input */}
      {field.widget === 'number' && (
        <div className="flex items-center gap-3">
          <input
            readOnly={readOnly}
            type="number"
            value={Number(displayValue)}
            onChange={(e) => handleChange(Number(e.target.value))}
            className={
              'w-full h-11 rounded-xl border border-border/80 ' +
              'bg-background px-4 text-sm text-foreground font-mono ' +
              'focus:outline-none focus:ring-2 focus:ring-violet-500/30 ' +
              'focus:border-violet-400 transition-all'
            }
          />
          {(field.min !== undefined || field.max !== undefined) && (
            <span className="text-[10px] text-muted-foreground whitespace-nowrap font-mono">
              {field.min ?? '—'} – {field.max ?? '—'}
            </span>
          )}
        </div>
      )}

      {/* Slider */}
      {field.widget === 'slider' &&
        (() => {
          // For users (not readOnly): use field.max as the constraint
          // For readOnly preview: show full range with enrichedMax if available
          const sliderMax = readOnly ? (field.enrichedMax ?? field.max ?? 0) : (field.max ?? 0);
          const currentValue = Math.min(Number(displayValue), sliderMax);

          return (
            <div className="space-y-2 pt-1">
              <div className="flex items-center gap-4">
                <input
                  type="range"
                  readOnly={readOnly}
                  disabled={readOnly}
                  min={field.min ?? 0}
                  max={sliderMax}
                  step={field.step ?? 1}
                  value={currentValue}
                  onChange={(e) => handleChange(Number(e.target.value))}
                  className="flex-1 h-2 accent-violet-600 cursor-pointer"
                />
                <span className="text-sm font-mono text-foreground bg-accent/50 px-2.5 py-1 rounded-lg min-w-[52px] text-center">
                  {String(displayValue)}
                </span>
              </div>
              <div className="flex justify-between text-[10px] text-muted-foreground/50 px-0.5 font-mono">
                <span>{field.min ?? 0}</span>
                <span>{sliderMax}</span>
              </div>
            </div>
          );
        })()}

      {/* Toggle */}
      {field.widget === 'toggle' && (
        <button
          type="button"
          disabled={readOnly}
          onClick={() => handleChange(!displayValue)}
          className={
            'flex items-center gap-3 w-full rounded-xl py-3 px-4 ' +
            'border transition-all cursor-pointer ' +
            (displayValue
              ? 'border-violet-300/50 bg-violet-50/50 dark:border-violet-500/30 dark:bg-violet-500/10'
              : 'border-border/60 bg-background')
          }
        >
          <div
            className={
              'w-11 h-6 rounded-full relative transition-colors shrink-0 ' + (displayValue ? 'bg-violet-600' : 'bg-muted')
            }
          >
            <div
              className={
                'absolute top-1 w-4 h-4 rounded-full bg-white transition-transform shadow-sm ' +
                (displayValue ? 'left-6' : 'left-1')
              }
            />
          </div>
          <span className={'text-sm ' + (displayValue ? 'text-foreground font-medium' : 'text-muted-foreground')}>
            {displayValue ? 'Enabled' : 'Disabled'}
          </span>
        </button>
      )}

      {/* Seed */}
      {field.widget === 'seed' && (
        <div className="flex items-center gap-2">
          <input
            readOnly={readOnly}
            type="number"
            value={Number(displayValue)}
            onChange={(e) => handleChange(Number(e.target.value))}
            className={
              'flex-1 h-11 rounded-xl border border-border/80 ' +
              'bg-background px-4 text-sm text-foreground font-mono ' +
              'focus:outline-none focus:ring-2 focus:ring-violet-500/30 ' +
              'focus:border-violet-400 transition-all'
            }
          />
          <Badge variant="outline" className="shrink-0 text-[10px] px-2.5 py-1.5 rounded-lg">
            <Dices className="h-3 w-3 mr-1" />
            Seed
          </Badge>
        </div>
      )}

      {/* Select */}
      {field.widget === 'select' && field.options && (
        <div className="relative">
          <select
            disabled={readOnly}
            value={String(displayValue)}
            onChange={(e) => handleChange(e.target.value)}
            className={
              'w-full h-11 rounded-xl border border-border/80 ' +
              'bg-background px-4 pr-10 text-sm text-foreground ' +
              'focus:outline-none focus:ring-2 focus:ring-violet-500/30 ' +
              'focus:border-violet-400 transition-all appearance-none cursor-pointer'
            }
          >
            {field.options.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        </div>
      )}

      {/* File inputs */}
      {(field.widget === 'file-image' || field.widget === 'file-video' || field.widget === 'file-audio') && (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptFor(uploadKind)}
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
          {uploadProgress?.status === 'uploading' ? (
            <div
              className={
                'rounded-xl border-2 border-violet-300/50 dark:border-violet-500/30 p-4 ' +
                'bg-violet-50/50 dark:bg-violet-500/10'
              }
            >
              <div className="flex items-center gap-3 mb-2">
                <Loader2 className="h-4 w-4 animate-spin text-violet-500" />
                <span className="text-sm text-foreground">Uploading...</span>
              </div>
              <div className="w-full bg-accent rounded-full h-2">
                <div
                  className="bg-violet-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress.progress}%` }}
                />
              </div>
            </div>
          ) : (displayValue as UploadedFileData)?.storagePath ? (
            <div className={'rounded-xl overflow-hidden ' + 'border-2 border-violet-300/50 dark:border-violet-500/30'}>
              {previewUrl && field.widget === 'file-image' && (
                <img
                  src={previewUrl}
                  alt={(displayValue as UploadedFileData).fileName}
                  className="w-full max-h-56 object-cover"
                />
              )}
              <div className={'flex items-center gap-3 px-4 py-2.5 ' + 'bg-violet-50/50 dark:bg-violet-500/10'}>
                {field.widget === 'file-image' && <ImagePlus className="h-4 w-4 text-violet-500 shrink-0" />}
                {field.widget === 'file-video' && <FileVideo className="h-4 w-4 text-violet-500 shrink-0" />}
                {field.widget === 'file-audio' && <FileAudio className="h-4 w-4 text-violet-500 shrink-0" />}
                <span className="text-sm text-foreground truncate flex-1">{(displayValue as UploadedFileData).fileName}</span>
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => handleChange(null)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div
              role="button"
              tabIndex={readOnly ? -1 : 0}
              onClick={() => !readOnly && fileInputRef.current?.click()}
              onKeyDown={(e) => {
                if (!readOnly && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  fileInputRef.current?.click();
                }
              }}
              onDragOver={(e) => {
                if (readOnly) return;
                e.preventDefault();
                if (!isDragging) setIsDragging(true);
              }}
              onDragLeave={(e) => {
                // Ignore leave events bubbling from children.
                if (e.currentTarget.contains(e.relatedTarget as Node)) return;
                setIsDragging(false);
              }}
              onDrop={(e) => {
                if (readOnly) return;
                e.preventDefault();
                setIsDragging(false);
                const file = e.dataTransfer.files?.[0];
                // Only accept an allowlisted file of the matching kind; ignore others.
                if (file && matchesKind(file, uploadKind)) handleFile(file);
              }}
              className={
                'w-full rounded-xl transition-all ' +
                (readOnly
                  ? 'border-2 border-dashed border-border/40 bg-accent/30 py-8'
                  : 'border-2 border-dashed cursor-pointer py-10 ' +
                    (isDragging
                      ? 'border-violet-500 bg-violet-50/80 dark:bg-violet-500/20'
                      : 'border-violet-300/40 dark:border-violet-500/20 ' +
                        'bg-gradient-to-b from-violet-50/30 to-violet-50/10 ' +
                        'dark:from-violet-500/5 dark:to-transparent ' +
                        'hover:border-violet-400/60 hover:from-violet-50/50 ' +
                        'dark:hover:border-violet-500/40 dark:hover:from-violet-500/10'))
              }
            >
              <div className="flex flex-col items-center gap-3">
                <div
                  className={'w-12 h-12 rounded-xl flex items-center justify-center ' + 'bg-violet-100/80 dark:bg-violet-500/20'}
                >
                  {field.widget === 'file-image' && <ImagePlus className="h-5 w-5 text-violet-500" />}
                  {field.widget === 'file-video' && <FileVideo className="h-5 w-5 text-violet-500" />}
                  {field.widget === 'file-audio' && <FileAudio className="h-5 w-5 text-violet-500" />}
                </div>
                {readOnly ? (
                  <p className="text-sm text-muted-foreground">File provided at runtime</p>
                ) : (
                  <>
                    <p className="text-sm font-medium text-foreground">
                      {field.widget === 'file-image'
                        ? 'Drop your image here or click to browse'
                        : field.widget === 'file-video'
                          ? 'Drop your video here or click to browse'
                          : 'Drop your audio file here or click to browse'}
                    </p>
                    {previousFileName ? (
                      <p className="text-xs text-violet-500 dark:text-violet-400 mt-1">Previously used: {previousFileName}</p>
                    ) : (
                      <p className="text-xs text-muted-foreground/60">
                        {field.widget === 'file-image'
                          ? 'PNG, JPG, WEBP up to 20MB'
                          : field.widget === 'file-video'
                            ? 'MP4, WEBM up to 100MB'
                            : 'MP3, WAV, FLAC'}
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
